import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import DailyRecordLog from './components/DailyRecordLog';
import MonthlyInvoice from './components/MonthlyInvoice';
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription, DialogFooter, DialogHeader } from "./components/ui/dialog";
import { Settings, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Trash2, FilePlus, Calendar as CalendarIcon, Mail } from 'lucide-react';
import { Input } from "./components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./components/ui/select";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "./components/ui/popover";
import { Alert, AlertDescription } from "./components/ui/alert";
import { format } from "date-fns";
import PracticeSettings from './components/PracticeSettings';
import { generateInvoicePDF } from './InvoicePDFGenerator';
import ProfileSettings from './ProfileSettings';
import { getFirestore, doc, getDoc, updateDoc, collection, addDoc, runTransaction } from 'firebase/firestore';
import { practiceDataUtil } from './helpers/practiceDataUtil';
import { getAuth } from 'firebase/auth';
import { localStorageUtil } from './helpers/localStorageUtil';
import PrivacyPolicy from './PrivacyPolicy';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { encryptData, decryptData } from './encryptionUtil';
import { getNextInvoiceNumber } from './invoiceUtils';
import { reportService } from './services/reportService';
import { LoadingSpinner } from './components/ui/loading-spinner';
import { cn } from './lib/utils';
import { db } from './firebase';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useToast } from './hooks/use-toast';
import { usePracticeSync } from './hooks/usePracticeSync'; // Add this import

const auth = getAuth();

// Move helper functions outside component
const sendInvoiceEmail = async (email) => {
  const functions = getFunctions();
  const sendInvoiceEmailFunction = httpsCallable(functions, 'sendInvoiceEmail');
  
  try {
    console.log('Calling sendInvoiceEmail with:', { email });
    const result = await sendInvoiceEmailFunction({ email });
    console.log('Raw result from cloud function:', result);
    return result.data;
  } catch (error) {
    console.error('Error sending invoice email:', error);
    throw error;
  }
};

const formatDate = (date) => {
  if (!date) return '';
  return format(date, "dd/MM/yyyy");
};

const calendarStyles = {
  '.rdp-nav_button': {
    position: 'absolute',
    top: '0.5rem',
  },
  '.rdp-nav_button_previous': {
    left: '0.5rem',
  },
  '.rdp-nav_button_next': {
    right: '0.5rem',
  },
};

const PrivacyPolicyLink = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="text-sm text-purple-600 hover:underline">View Privacy Policy</button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl">
      <PrivacyPolicy />
    </DialogContent>
  </Dialog>
);

const HygienistInvoice = ({ user, initialSelectedPractices = [] }) => {
  // Move practice state above all other state declarations
  const [practiceState, setPracticeState] = useState({
    loading: false,
    initialized: false,
    selectedPractice: null,
    selectedPractices: [],
    filteredPractices: [],
    error: null,
    timestamp: null
  });

  // Extract values AFTER state is initialized
  const { selectedPractice, selectedPractices, filteredPractices } = practiceState;

  // Move ref after state and destructuring
  const prevPracticesCountRef = useRef(selectedPractices?.length || 0);

  const { toast } = useToast();
  // Group related state together
  // UI state
  const [activeTab, setActiveTab] = useState('monthly');
  const [showPracticeManagement, setShowPracticeManagement] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPracticeSettings, setShowPracticeSettings] = useState(false);
  const [showProfileWarning, setShowProfileWarning] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [error, setError] = useState('');
  
  // Loading states
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  
  // Profile data
  const [profileData, setProfileData] = useState(null);
  
  // Invoice state
  const [rateType, setRateType] = useState('percentage');
  const [rateValue, setRateValue] = useState('');
  const [entries, setEntries] = useState([
    { date: null, hours: '', patients: [{ count: '', rate: '', description: '' }], isExpanded: true }
  ]);
  const [total, setTotal] = useState(null);
  const [summary, setSummary] = useState([]);
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState(null);
  const [invoiceSaved, setInvoiceSaved] = useState(false);
  const [savedInvoiceData, setSavedInvoiceData] = useState(null);
  const [treatmentDate, setTreatmentDate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState({
    includeHeader: true,
    includeFooter: true,
    includeBankDetails: true
  });
  
  // Email state
  const [emailStatus, setEmailStatus] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [saveInvoiceToCustomers, setSaveInvoiceToCustomers] = useState(true);
  
  // Calendar state
  const [openCalendar, setOpenCalendar] = useState(null);
  
  // Add loading state tracking
  const [practiceInitState, setPracticeInitState] = useState({
    loading: false,
    initialized: false,
    timestamp: null
  });

  // Add practicesLoading state
  const [practicesLoading, setPracticesLoading] = useState(false);
  
  // Add practicesError state
  const [practicesError, setPracticesError] = useState(null);

  // Consolidated practice state management
  const updatePracticeState = (updates) => {
    setPracticeState(prev => ({
      ...prev,
      ...updates
    }));
  };

  // Replace all direct state updates with updatePracticeState
  const setSelectedPractice = (practiceCode) => {
    updatePracticeState({ selectedPractice: practiceCode });
  };

  const setSelectedPractices = (practices) => {
    updatePracticeState({ selectedPractices: practices });
  };

  const setFilteredPractices = (practices) => {
    updatePracticeState({ filteredPractices: practices });
  };

  // Function to fetch the latest profile data
  const fetchLatestProfileData = async () => {
    if (auth.currentUser) {
      try {
        const userDoc = doc(db, 'users', auth.currentUser.uid);
        const userSnapshot = await getDoc(userDoc);
        
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          
          // Initialize decryptedData with non-sensitive fields
          const decryptedData = {
            ...userData,
            uid: auth.currentUser.uid,
            email: auth.currentUser.email
          };
  
          // Process sensitive fields
          const sensitiveFields = [
            'name', 'address1', 'address2', 'city', 'postcode',
            'bankName', 'accountNumber', 'sortCode', 'lastInvoiceNumber'
          ];
  
          for (const field of sensitiveFields) {
            if (userData[field]) {
              try {
                if (userData[field].startsWith('U2FsdGVkX1')) {
                  const decryptedValue = decryptData(userData[field]);
                  decryptedData[field] = decryptedValue || '';
                  console.log(`Decrypted ${field}`);
                } else {
                  decryptedData[field] = userData[field];
                  console.log(`Field ${field} not encrypted`);
                }
              } catch (error) {
                console.error(`Error decrypting ${field}:`, error);
                decryptedData[field] = '';
              }
            } else {
              decryptedData[field] = '';
            }
          }
  
          setProfileData(decryptedData);
          // Store the encrypted version in localStorage
          localStorageUtil.setItem(`userData_${auth.currentUser.uid}`, userData);
        }
      } catch (error) {
        console.error('Error in fetchLatestProfileData:', error);
        setProfileData(null);
      }
    }
  };

  // Handle saving profile data
  const handleSaveProfile = async (data) => {
    setProfileData(data);
    setShowProfileSettings(false);
    await fetchLatestProfileData(); // Fetch the latest data after saving
  };

  // --- PRACTICE MANAGEMENT LOGIC ---
  
  // Single, consolidated practice selection handler
  const handlePracticeSelection = async (newSelectedPractices) => {
    if (!Array.isArray(newSelectedPractices)) return;

    try {
      console.log(`[PRACTICE-DEBUG] handlePracticeSelection received ${newSelectedPractices.length} practices`);
      
      const savedSelPractice = practiceState.selectedPractice;
      
      // Update state with new selection
      const stateUpdate = {
        selectedPractices: newSelectedPractices,
        filteredPractices: newSelectedPractices
      };

      if (newSelectedPractices.length > 0) {
        const currentExists = newSelectedPractices.some(p => p.prac_code === savedSelPractice);
        if (!currentExists) {
          const newSelected = newSelectedPractices[0].prac_code;
          console.log(`[PRACTICE-DEBUG] Changing selected practice from ${savedSelPractice} to ${newSelected}`);
          stateUpdate.selectedPractice = newSelected;
          localStorage.setItem('selectedPractice', newSelected);
        }
      } else {
        stateUpdate.selectedPractice = null;
        localStorage.removeItem('selectedPractice');
      }

      // Update state in one batch
      updatePracticeState(stateUpdate);

      // Save practice codes to localStorage
      localStorage.setItem('selectedPractices', 
        JSON.stringify(newSelectedPractices.map(p => p.prac_code))
      );
      
      // Save to Firestore if user is authenticated
      if (user?.uid) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          selectedPractices: newSelectedPractices.map(p => p.prac_code),
          lastUpdated: new Date()
        });
        
        // Update the practiceDataUtil cache as well
        practiceDataUtil.setUserPractices(user.uid, newSelectedPractices);
      }

      toast({
        title: "Success",
        description: "Practice selection updated",
        variant: "success",
      });
    } catch (error) {
      console.error('[PRACTICE-DEBUG] Error saving practice selection:', error);
      toast({
        title: "Error",
        description: "Failed to save practice selection",
        variant: "destructive",
      });
    }
  };

  // Update practice change handler to work with selection logic
  const handlePracticeChange = useCallback((pracCode) => {
    if (!pracCode) return;
    
    console.log('Practice changed to:', pracCode);
    
    const practice = practiceState.selectedPractices.find(p => p.prac_code === pracCode);
    
    if (practice) {
      updatePracticeState({ selectedPractice: pracCode });
      localStorage.setItem('selectedPractice', pracCode);
    } else {
      console.error('Practice not found in selectedPractices:', pracCode);
      toast({
        title: "Error",
        description: "Selected practice not found",
        variant: "destructive",
      });
    }
  }, [practiceState.selectedPractices, toast]);

  // SINGLE consolidated practice initialization and loading effect
  useEffect(() => {
    if (!user?.uid) {
      console.log('[PRACTICE-DEBUG] No user available, skipping practice load');
      return;
    }

    let isMounted = true;
    setPracticesLoading(true);

    const initializePractices = async () => {
      try {
        // Single initialization
        await practiceDataUtil.initializePracticeData();
        
        if (!isMounted) return;

        // Get practices with enhancements in one go
        const practices = await practiceDataUtil.getUserPracticesInitialized(user.uid);
        
        if (!isMounted) return;

        if (practices?.length > 0) {
          // Update state with enhanced practices
          updatePracticeState({
            selectedPractices: practices,
            selectedPractice: practice => {
              const lastSelected = localStorage.getItem('selectedPractice');
              return lastSelected && practices.some(p => p.prac_code === lastSelected)
                ? lastSelected
                : practices[0].prac_code;
            }
          });

          // Update localStorage only if needed
          const currentSelected = localStorage.getItem('selectedPractice');
          if (!currentSelected || !practices.some(p => p.prac_code === currentSelected)) {
            localStorage.setItem('selectedPractice', practices[0].prac_code);
          }
        }

        // Update initialization state
        setPracticeInitState({
          loading: false,
          initialized: true,
          timestamp: Date.now()
        });
      } catch (error) {
        console.error('[PRACTICE-DEBUG] Error initializing practices:', error);
        if (isMounted) {
          setPracticesError("Failed to load practice data");
          setPracticeInitState(prev => ({ 
            ...prev, 
            loading: false,
            error: error.message 
          }));
        }
      } finally {
        if (isMounted) {
          setPracticesLoading(false);
        }
      }
    };

    initializePractices();
    return () => { isMounted = false; };
  }, [user?.uid]);

  // ENTRY MANAGEMENT FUNCTIONS
  const addEntry = () => {
    const newEntries = entries.map(entry => ({
      ...entry,
      isExpanded: false
    }));
    setEntries([...newEntries, { 
      date: null, 
      hours: '', 
      description: '',
      patients: [{ count: '', rate: '' }], 
      isExpanded: true 
    }]);
  };

  const updateEntry = (dateIndex, field, value) => {
    const newEntries = [...entries];
    if (field === 'date') {
      newEntries[dateIndex].date = value;
    } else if (field === 'hours') {
      newEntries[dateIndex].hours = value;
    } else if (field === 'description') { // Add this condition
      newEntries[dateIndex].description = value;
    } else {
      const [patientIndex, patientField] = field.split('.');
      newEntries[dateIndex].patients[patientIndex][patientField] = value;
    }
    setEntries(newEntries);
  };

  const deleteEntry = (indexToDelete) => {
    setEntries(entries.filter((_, index) => index !== indexToDelete));
  };

  const toggleExpand = (index) => {
    const newEntries = entries.map((entry, i) => 
      i === index ? {...entry, isExpanded: !entry.isExpanded} : entry
    );
    setEntries(newEntries);
  };

  const addMorePatients = (dateIndex) => {
    const newEntries = [...entries];
    newEntries[dateIndex].patients.push({ count: '', rate: '' });
    setEntries(newEntries);
  };

  const getEntrySummary = (entry) => {
    if (rateType === 'hourly') {
      return `${entry.hours || 0} hours`;
    } else {
      const totalPatients = entry.patients.reduce((sum, patient) => sum + (parseInt(patient.count) || 0), 0);
      return `${totalPatients} patients`;
    }
  };

  // INVOICE FUNCTIONS
  const calculateTotal = () => {
    console.log("Calculating total for entries:", entries);
  
    if (!entries || entries.length === 0 || !rateValue) {
      console.log("Missing entries or rate value");
      toast({
        title: "Missing Information",
        description: "Please add entries with dates and set a rate value",
        variant: "destructive",
      });
      return;
    }
  
    let totalHours = 0;
    let totalPatients = 0;
    let totalAmount = 0;
    const detailedSummary = [];
  
    // Filter entries that have dates and group by date
    const validEntries = entries.filter(entry => entry.date);
    const groupedEntries = validEntries.reduce((acc, entry) => {
      const dateKey = format(entry.date, "yyyy-MM-dd");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(entry);
      return acc;
    }, {});
  
    // Process each date group
    Object.entries(groupedEntries).forEach(([dateKey, dateEntries]) => {
      const formattedDate = format(dateEntries[0].date, "dd MMM yyyy");
      let dateCollections = 0; // For percentage: collections before percentage applied
      let dateTotal = 0;
      let datePatients = 0;
      let dateHours = 0;
      let dateLines = [];
  
      dateEntries.forEach(entry => {
        if (rateType === 'hourly') {
          const hours = parseFloat(entry.hours) || 0;
          const hourlyRate = parseFloat(rateValue) || 0;
          const entryAmount = hours * hourlyRate;
          dateHours += hours;
          dateTotal += entryAmount;
          dateLines.push(`${hours} hours × £${hourlyRate.toFixed(2)}`);
        } 
        else if (rateType === 'percentage') {
          console.log('🔥🔥🔥 NEW PERCENTAGE LOGIC RUNNING - UPDATED CODE 🔥🔥🔥');
          if (entry.patients && entry.patients.length > 0) {
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              const patientCollections = count * rate; // Collections without percentage
              
              datePatients += count;
              dateCollections += patientCollections; // Track collections for this date
              
              // Show patient details WITHOUT percentage
              const pluralText = count !== 1 ? 's' : '';
              const line = `${count} patient${pluralText} @ £${rate.toFixed(2)}${patient.description ? ` (${patient.description})` : ''}`;
              console.log('🔥 Building dateLines entry:', line);
              dateLines.push(line);
            });
          }
        }
        else if (rateType === 'fixed') {
          const fixedAmount = parseFloat(rateValue) || 0;
          dateTotal += fixedAmount;
          dateHours += parseFloat(entry.hours) || 0;
          dateLines.push(`Fixed rate: £${fixedAmount.toFixed(2)}`);
        }
      });
  
      // For percentage type, show collections (not percentage amount) in the daily summary
      if (rateType === 'percentage') {
        detailedSummary.push({
          label: formattedDate,
          value: `${dateLines.join(' + ')} = £${dateCollections.toFixed(2)}`
        });
        totalAmount += dateCollections; // Accumulate collections (will apply percentage later)
      } else {
        // For hourly and fixed, keep existing behavior
        detailedSummary.push({
          label: formattedDate,
          value: `${dateLines.join(' | ')} = £${dateTotal.toFixed(2)}`
        });
        totalAmount += dateTotal;
      }
  
      // Update totals
      totalHours += dateHours;
      totalPatients += datePatients;
    });
  
    // For percentage invoices, add summary rows and apply percentage
    if (rateType === 'percentage') {
      const percentageRate = parseFloat(rateValue) || 0;
      const totalCollections = totalAmount; // At this point, totalAmount = sum of all collections
      const percentageAmount = totalCollections * (percentageRate / 100);
      
      // Add subtotal row
      detailedSummary.push({
        label: 'Subtotal (patient fees)',
        value: `= £${totalCollections.toFixed(2)}`,
        isSubtotal: true
      });
      
      // Add percentage calculation row
      detailedSummary.push({
        label: `Your ${percentageRate}% commission`,
        value: `= £${percentageAmount.toFixed(2)}`,
        isPercentage: true
      });
      
      // Add total row
      detailedSummary.push({
        label: 'Invoice Total',
        value: `= £${percentageAmount.toFixed(2)}`,
        isTotal: true
      });
      
      // Update totalAmount to be the percentage amount
      totalAmount = percentageAmount;
    }
    
    console.log('🔥🔥🔥 FINAL SUMMARY GENERATED:', detailedSummary);
  
    // Update state with calculated values
    setTotal({
      hours: totalHours,
      patients: totalPatients,
      amount: totalAmount
    });
    setSummary(detailedSummary);
  
    toast({
      title: "Total Calculated",
      description: `Total amount: ${formatCurrency(totalAmount)}`,
      variant: "success",
    });
  };

  const handleGeneratePDF = async () => {
    if (!previewHtml || !profileData) return;
  
    setPdfLoading(true);
    try {
      // Get invoice number if needed
      let invoiceNumber;
      if (!currentInvoiceNumber) {
        try {
          invoiceNumber = await getNextInvoiceNumber(user.uid, profileData);
        } catch (error) {
          console.error("Error generating invoice number:", error);
          // Continue without invoice number if it fails
        }
      } else {
        invoiceNumber = currentInvoiceNumber;
      }
      
      // Create invoice element from HTML
      const element = document.createElement('div');
      element.innerHTML = previewHtml;
      element.style.width = '210mm'; // A4 width
      element.style.padding = '10mm';
      element.style.backgroundColor = 'white';
      document.body.appendChild(element);
      
      // Add invoice number if we have one
      if (invoiceNumber) {
        const invoiceNumberElement = document.createElement('div');
        invoiceNumberElement.style.fontSize = '12pt';
        invoiceNumberElement.style.marginBottom = '10mm';
        invoiceNumberElement.innerHTML = `<strong>Invoice #: ${invoiceNumber}</strong>`;
        element.insertBefore(invoiceNumberElement, element.firstChild);
      }
      
      // Convert to PDF using html2canvas and jsPDF
      const canvas = await html2canvas(element, {
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
      });
      document.body.removeChild(element);
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        format: 'a4',
        unit: 'mm',
      });
      
      // Calculate dimensions
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      // Generate filename with practice name and date
      const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
      const practiceName = practice ? practice.prac_name.replace(/\s+/g, '_') : 'invoice';
      const dateSuffix = format(new Date(), 'yyyy-MM-dd');
      const filename = `${practiceName}_invoice_${dateSuffix}.pdf`;
      
      pdf.save(filename);
      
      toast({
        title: "Success",
        description: "PDF generated successfully",
        variant: "success",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const saveInvoice = async (formattedSummary = null) => {
    if (selectedPractice && entries.length > 0) {
      try {
        setGeneratingPDF(true);
        setError(null);
        
        const selectedPracticeDetails = selectedPractices.find(p => p.prac_code === selectedPractice);
        
        if (selectedPracticeDetails) {
          // For saving the invoice, get a fresh number with persistence
          const invoiceNumberService = require('./services/invoiceNumberService').invoiceNumberService;
          const newInvoiceNumber = await invoiceNumberService.generateInvoiceNumber(
            auth.currentUser.uid, 
            profileData, 
            { forSaving: true, persistToDatabase: true }
          );
          
          if (!newInvoiceNumber) {
            setError('Failed to generate invoice number');
            setGeneratingPDF(false);
            return null;
          }
          
          // Use provided formatted summary if available, otherwise convert from the existing summary
          let summaryToSave = formattedSummary;
          
          // If no formatted summary was provided, convert the current summary
          if (!summaryToSave) {
            // Group entries by date
            const entriesByDate = entries.reduce((acc, entry) => {
              if (!entry.date) return acc;
              
              const dateKey = format(entry.date, "yyyy-MM-dd");
              if (!acc[dateKey]) {
                acc[dateKey] = [];
              }
              acc[dateKey].push(entry);
              return acc;
            }, {});
            
            // Convert to proper format for saving
            summaryToSave = Object.entries(entriesByDate).map(([dateKey, dateEntries]) => {
              const date = dateEntries[0].date;
              const formattedDate = format(date, "dd MMM yyyy");
              
              // For hourly/fixed rates
              if (rateType === 'hourly' || rateType === 'fixed') {
                const hours = dateEntries.reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0);
                const amount = rateType === 'hourly' 
                  ? hours * parseFloat(rateValue || 0)
                  : parseFloat(rateValue || 0);
                  
                return {
                  date,
                  formattedDate,
                  amount: amount.toString(),
                  patients: [{
                    count: hours,
                    rate: parseFloat(rateValue || 0),
                    description: dateEntries[0].description || 'Hours worked',
                    rateType,
                    rateValue: parseFloat(rateValue || 0)
                  }],
                  totalPatients: 0, // Not applicable for hourly/fixed
                  rateType,
                  rateValue: parseFloat(rateValue || 0)
                };
              } 
              // For percentage rates - FIXED to include ALL patient entries
              else {
                // Collect all patient entries across all entries for this date
                const allPatients = [];
                let totalPatients = 0;
                let totalAmount = 0;
                
                dateEntries.forEach(entry => {
                  if (entry.patients && entry.patients.length > 0) {
                    entry.patients.forEach(patient => {
                      if (!patient.count || !patient.rate) return;
                      
                      const count = parseInt(patient.count || 0);
                      const rate = parseFloat(patient.rate || 0);
                      const subtotal = count * rate;
                      const percentageRate = parseFloat(rateValue || 0);
                      const patientAmount = subtotal * (percentageRate / 100);
                      
                      allPatients.push({
                        count,
                        rate,
                        description: patient.description || '',
                        rateType: 'percentage',
                        rateValue: percentageRate
                      });
                      
                      totalPatients += count;
                      totalAmount += patientAmount;
                    });
                  }
                });
                
                return {
                  date,
                  formattedDate,
                  amount: totalAmount.toString(),
                  patients: allPatients,
                  totalPatients,
                  rateType,
                  rateValue: parseFloat(rateValue || 0)
                };
              }
            });
          }
  
          // Calculate total from all entries
          const calculatedTotal = summaryToSave.reduce((sum, item) => 
            sum + parseFloat(item.amount || 0), 0);
  
          const invoiceData = {
            practice: selectedPracticeDetails,
            entries: summaryToSave,
            total: calculatedTotal,
            invoiceNumber: newInvoiceNumber,
            profile: profileData,
            rateType,
            rateValue
          };
  
          // Store invoice data for reporting only if not already saved
          if (!invoiceSaved) {
            const invoiceRef = collection(db, 'invoices');
            await addDoc(invoiceRef, {
              userId: user.uid,
              practiceId: selectedPractice,
              totalAmount: calculatedTotal,
              createdAt: new Date(),
              practiceDetails: selectedPracticeDetails,
              invoiceNumber: newInvoiceNumber,
              rateType,
              rateValue,
              entriesCount: entries.length,
              summaryData: summaryToSave // Now with ALL patient entries per date
            });
          }
  
          setSavedInvoiceData(invoiceData);
          setCurrentInvoiceNumber(newInvoiceNumber);
          setInvoiceSaved(true);
          setGeneratingPDF(false);
          return invoiceData;
        }
      } catch (error) {
        console.error('Error saving invoice:', error);
        setError('An error occurred while saving the invoice. Please try again.');
        setGeneratingPDF(false);
        return null;
      }
    } else {
      setError('Please ensure all required data is available before saving.');
      return null;
    }
  };

  const generateInvoiceNumber = async () => {
    try {
      // Make sure we have profile data available
      if (!profileData || !profileData.name) {
        throw new Error('Profile data not available');
      }
  
      console.log('Generating invoice number for profile:', profileData.name);
      
      // For preview, use regular generation (may use cache)
      const formattedNumber = await getNextInvoiceNumber(auth.currentUser.uid, profileData);
      console.log('Generated new invoice number:', formattedNumber);
      
      setCurrentInvoiceNumber(formattedNumber);
      return formattedNumber;
    } catch (error) {
      console.error('Error generating invoice number:', error);
      // Fallback to a basic format with timestamp in case of error
      const timestamp = new Date().getTime().toString().slice(-6);
      const fallbackNumber = `INV${timestamp}`;
      console.log('Using fallback invoice number:', fallbackNumber);
      return fallbackNumber;
    }
  };

  const handleEmailInvoice = async () => {
    try {
      setIsEmailSending(true);
      setEmailStatus('Sending...');
  
      if (!summary.length || !total || !selectedPractice || !profileData || !currentInvoiceNumber) {
        throw new Error('Missing required data for invoice generation');
      }
  
      const practiceDetails = selectedPractices.find(practice => practice.prac_code === selectedPractice);
      if (!practiceDetails) {
        throw new Error('Selected practice details not found');
      }
      // Use the decrypted lastInvoiceNumber from profileData
      let latestInvoiceNumber = profileData.lastInvoiceNumber || currentInvoiceNumber;
  
      // Double-check the latest invoice number from Firestore
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.lastInvoiceNumber) {
          const decryptedLastNumber = decryptData(userData.lastInvoiceNumber);
          if (decryptedLastNumber > currentInvoiceNumber) {
            latestInvoiceNumber = decryptedLastNumber;
          }
        }
      }
  
      const invoiceData = {
        summary,
        total,
        practiceDetails,
        rateType,
        userProfile: {
          ...profileData,
          lastInvoiceNumber: latestInvoiceNumber
        },
        invoiceNumber: latestInvoiceNumber
      };
  
      console.log('Sending email with invoice data:', invoiceData);
      const result = await sendInvoiceEmail(invoiceData);
      
      console.log('Email send result:', result);
  
      if (result && result.success) {
        setEmailStatus('Invoice sent successfully!');
        setCurrentInvoiceNumber(result.newInvoiceNumber);
        
        // Update profileData with the confirmed invoice number from the server
        const updatedProfileData = {
          ...profileData,
          lastInvoiceNumber: result.newInvoiceNumber
        };
        setProfileData(updatedProfileData);
        localStorageUtil.setItem(`userData_${auth.currentUser.uid}`, {
          ...updatedProfileData,
          lastInvoiceNumber: encryptData(result.newInvoiceNumber) // Encrypt before storing
        });
        
        // Fetch the latest user data to ensure local state is up-to-date
        await fetchLatestUserData();
      } else {
        setEmailStatus(`Failed to send invoice: ${result?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending invoice email:', error);
      setEmailStatus(`Failed to send invoice: ${error.message}`);
    } finally {
      setIsEmailSending(false);
    }
  };

  const fetchLatestUserData = async () => {
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        const latestUserData = userDoc.data();
        setProfileData(prevProfile => ({
          ...prevProfile,
          lastInvoiceNumber: latestUserData.lastInvoiceNumber
        }));
        localStorageUtil.setItem(`userData_${auth.currentUser.uid}`, latestUserData);
      }
    } catch (error) {
      console.error('Error fetching latest user data:', error);
    }
  };

  // UTILITY FUNCTIONS
  const formatSummaryDate = (date) => {
    if (!date) return '';
    return format(date, "dd/MM/yy");
  };

  // Add useEffect to handle initialSelectedPractices changes
  useEffect(() => {
    updatePracticeState({
      selectedPractices: initialSelectedPractices,
      selectedPractice: initialSelectedPractices.length > 0 
        ? (!selectedPractice || !initialSelectedPractices.some(p => p.prac_code === selectedPractice)
            ? initialSelectedPractices[0].prac_code
            : selectedPractice)
        : null
    });
  }, [initialSelectedPractices, selectedPractice]);

  const storeInvoiceData = async (invoiceData, retryCount = 3) => {
    try {
      const invoiceRef = collection(db, 'invoices');
      await addDoc(invoiceRef, {
        ...invoiceData,
        createdAt: new Date(),
      });
      return true;
    } catch (error) {
      console.error('Error storing invoice data:', error);
      if (retryCount > 0) {
        // Wait for 1 second before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
        return storeInvoiceData(invoiceData, retryCount - 1);
      }
      throw error;
    }
  };

  // Add new function to handle creating new invoice
  const handleNewInvoice = () => {
    setInvoiceSaved(false);
    setSavedInvoiceData(null);
    setEntries([{ 
      date: null, 
      hours: '', 
      patients: [{ count: '', rate: '', description: '' }], 
      isExpanded: true 
    }]);
    setSummary([]);
    setTotal(null);
  };

  // Add handler for showing settings
  const handleShowSettings = () => {
    setShowSettings(true);
  };

  // Format currency utility
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  // Add an effect to initialize selectedPractice when selectedPractices changes
  useEffect(() => {
    if (!selectedPractice && selectedPractices.length > 0) {
      // Try to get from localStorage first
      const savedPractice = localStorage.getItem('selectedPractice');
      
      if (savedPractice && selectedPractices.some(p => p.prac_code === savedPractice)) {
        setSelectedPractice(savedPractice);
      } else {
        // Default to first practice
        setSelectedPractice(selectedPractices[0].prac_code);
      }
    }
  }, [selectedPractices, selectedPractice]);

  // Add handler for date change
  const handleDateChange = (entryIndex, date) => {
    const newEntries = [...entries];
    newEntries[entryIndex] = { ...newEntries[entryIndex], date };
    setEntries(newEntries);
  };

  // Add handler for generate preview
  const handleGeneratePreview = async () => {
    if (!profileData) {
      toast({
        title: "Error",
        description: "Please complete your profile before generating an invoice",
        variant: "destructive",
      });
      return;
    }
  
    if (!total) {
      toast({
        title: "Error",
        description: "Please calculate the total first",
        variant: "destructive",
      });
      return;
    }
  
    setPreviewLoading(true);
  
    try {
      // Get practice details from the utility to ensure we have the address
      let practice = null;
      if (selectedPractice) {
        practice = await practiceDataUtil.getPracticeByCode(selectedPractice);
        if (!practice) {
          // Fall back to the selected practices array if needed
          practice = selectedPractices.find(p => p.prac_code === selectedPractice);
        }
      }
      
      if (!practice) {
        toast({
          title: "Error",
          description: "Practice not found. Please try selecting a practice again.",
          variant: "destructive",
        });
        setPreviewLoading(false);
        return;
      }
      
      // Generate a new invoice number if needed
      let invoiceNum = currentInvoiceNumber;
      if (!invoiceNum) {
        try {
          invoiceNum = await generateInvoiceNumber();
          setCurrentInvoiceNumber(invoiceNum);
        } catch (error) {
          console.error("Error generating invoice number:", error);
        }
      }
  
      // Format the invoice date
      const invoiceDate = new Date();
      const formattedDate = format(invoiceDate, "dd MMMM yyyy");
      
      // Format practice address properly - excluding any NA or undefined values
      const addressLines = [];
      if (practice.prac_name) addressLines.push(`<strong>${practice.prac_name}</strong>`);
      if (practice.address1 && practice.address1 !== 'NA') addressLines.push(practice.address1);
      if (practice.address2 && practice.address2 !== 'NA') addressLines.push(practice.address2);
      if (practice.address3 && practice.address3 !== 'NA') addressLines.push(practice.address3);
      if (practice.address4 && practice.address4 !== 'NA') addressLines.push(practice.address4);
      if (practice.address5 && practice.address5 !== 'NA') addressLines.push(practice.address5);
      
      // Add city and postcode on the same line if both exist
      const locationLine = [
        practice.prac_city && practice.prac_city !== 'NA' ? practice.prac_city : '', 
        practice.prac_postcode && practice.prac_postcode !== 'NA' ? practice.prac_postcode : '',
        practice.postcode && practice.postcode !== 'NA' ? practice.postcode : ''
      ].filter(Boolean).join(' ');
      
      if (locationLine) addressLines.push(locationLine);
      
      const formattedAddress = addressLines.join('<br>');
      
      // Generate the HTML content
      let htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
            <div>
              <h2 style="color: #4b0082; margin: 0;">${profileData.name || 'Your Name'}</h2>
              <p style="margin: 5px 0; color: #333;">
                ${profileData.address1 || ''}<br>
                ${profileData.city || ''}, ${profileData.postcode || ''}
              </p>
              <p style="margin: 5px 0; color: #333;">
                Email: ${profileData.email || ''}<br>
                Phone: ${profileData.phone || ''}
              </p>
            </div>
            <div style="text-align: right;">
              <h1 style="color: #4b0082; margin: 0;">INVOICE</h1>
              <p style="margin: 5px 0; color: #333;">Date: ${formattedDate}</p>
              <p style="margin: 5px 0; color: #333;">Invoice #: ${invoiceNum || 'TBD'}</p>
            </div>
          </div>
  
          <div style="background-color: #f9f9f9; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
            <h3 style="margin: 0 0 10px; color: #4b0082;">Bill To:</h3>
            <p style="margin: 0; color: #333;">
              ${formattedAddress}
            </p>
          </div>
  
          <!-- Rest of the invoice HTML -->
      `;
  
      // Complete the invoice HTML with entries, totals, etc.
      // ...existing invoice HTML generation...
  
      // Set the generated HTML to the state
      setPreviewHtml(htmlContent);
      
      toast({
        title: "Success",
        description: "Invoice preview has been generated",
        variant: "success",
      });
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: "Error",
        description: "Failed to generate invoice preview",
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Add handlers for patient management
  const handlePatientChange = (entryIndex, patientIndex, field, value) => {
    const newEntries = [...entries];
    newEntries[entryIndex].patients[patientIndex][field] = value;
    setEntries(newEntries);
  };

  const handleRemovePatient = (entryIndex, patientIndex) => {
    const newEntries = [...entries];
    if (newEntries[entryIndex].patients.length > 1) {
      newEntries[entryIndex].patients.splice(patientIndex, 1);
      setEntries(newEntries);
    }
  };

// IMPROVED: Add effect to save selected practices when they change
useEffect(() => {
  const savePracticeSelection = async () => {
    if (!user?.uid || !selectedPractices?.length) return;
    
    try {
      // Save the practice codes both to Firestore and localStorage
      const practiceCodes = selectedPractices.map(p => p.prac_code);
      
      // Update Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        selectedPractices: practiceCodes,
        lastUpdated: new Date()
      });
      
      // Update localStorage
      localStorage.setItem('selectedPractices', JSON.stringify(practiceCodes));
      
      console.log(`Saved ${practiceCodes.length} selected practices for user ${user.uid}`);
    } catch (error) {
      console.error('Error saving practice selection:', error);
    }
  };
  
  if (selectedPractices?.length) {
    savePracticeSelection();
  }
}, [selectedPractices, user?.uid, db]);

// SINGLE CONSOLIDATED practice loading effect
useEffect(() => {
  if (!user?.uid) {
    console.log('[PRACTICE-DEBUG] No user available, skipping practice load');
    return;
  }

  let isMounted = true;
  setPracticesLoading(true);
  
  const loadPractices = async () => {
    try {
      const practices = await practiceDataUtil.getUserPractices(user.uid);
      
      if (!isMounted) return;

      if (practices?.length > 0) {
        setSelectedPractices(practices);
        
        // Get last selected practice from localStorage
        const lastSelected = localStorage.getItem('selectedPractice');
        if (lastSelected && practices.some(p => p.prac_code === lastSelected)) {
          setSelectedPractice(lastSelected);
        } else {
          setSelectedPractice(practices[0].prac_code);
          localStorage.setItem('selectedPractice', practices[0].prac_code);
        }
      } else {
        setSelectedPractices([]);
        setSelectedPractice(null);
      }
    } catch (error) {
      console.error('[PRACTICE-DEBUG] Error loading practices:', error);
      if (isMounted) {
        setPracticesError("Failed to load practice data");
      }
    } finally {
      if (isMounted) {
        setPracticesLoading(false);
      }
    }
  };

  loadPractices();
  return () => { isMounted = false; };
}, [user?.uid]);

// ADD DEBUG PANEL THAT CAN BE TOGGLED ON/OFF
const DebugPanel = React.memo(({ selectedPractices, selectedPractice }) => {
  return (
    <div className="mb-4 p-3 bg-gray-800 border border-gray-700 rounded text-xs">
      <details>
        <summary className="text-purple-400 cursor-pointer">Debug Info</summary>
        <div className="mt-2 text-gray-300 space-y-1">
          <div>Selected Practices: {Array.isArray(selectedPractices) ? selectedPractices.length : 0}</div>
          {Array.isArray(selectedPractices) && selectedPractices.length > 0 && (
            <ul className="list-disc list-inside">
              {selectedPractices.slice(0, 5).map(p => (
                <li key={p.prac_code} className={selectedPractice === p.prac_code ? "text-purple-400" : ""}>
                  {p.prac_name} ({p.prac_code})
                </li>
              ))}
              {selectedPractices.length > 5 && <li>...and {selectedPractices.length - 5} more</li>}
            </ul>
          )}
          <div>Selected Practice: {selectedPractice || 'none'}</div>
        </div>
      </details>
    </div>
  );
});

// IMPROVE PRACTICE SETTINGS TRIGGER FUNCTION
const handlePracticeSettings = () => {
  console.log('[PRACTICE-DEBUG] Opening PracticeSettings with:', {
    practiceCount: selectedPractices.length,
    selectedPractice
  });
  
  // Show the practice settings dialog
  setShowPracticeSettings(true);
};

// Add usePracticeSync hook if not already present
const { getPractice: originalGetPractice } = usePracticeSync({ autoInitialize: true });
  
// IMPORTANT: Memoize the getPractice function to prevent infinite re-rendering
const memoizedGetPractice = useCallback((practiceCode) => {
  if (!practiceCode) return null;
  return originalGetPractice(practiceCode);
}, [originalGetPractice]);

// IMPORTANT: Memoize all props passed to MonthlyInvoice to stabilize them
const monthlyInvoiceProps = useMemo(() => ({
  user,
  selectedPractices,
  selectedPractice,
  setSelectedPractice,
  profileData,
  showProfileWarning,
  showProfileSettings,
  setShowProfileSettings,
  handleSaveProfile,
  entries,
  setEntries,
  rateType,
  setRateType,
  rateValue,
  setRateValue,
  total,
  setTotal,
  summary,
  setSummary,
  handleAddEntry: addEntry,
  handleRemoveEntry: deleteEntry,
  handleEntryChange: updateEntry,
  handlePatientChange,
  handleAddPatient: addMorePatients,
  handleRemovePatient,
  handleToggleEntry: toggleExpand,
  handleDateChange,
  handleCalculateTotal: calculateTotal,
  handleGeneratePreview,
  handleSaveInvoice: saveInvoice,
  handleSendInvoice: handleEmailInvoice,
  handlePracticeChange,
  handleShowSettings,
  formatCurrency,
  showSettings,
  setShowSettings,
  treatmentDate,
  setTreatmentDate,
  previewHtml,
  setPreviewHtml,
  invoiceSettings,
  setInvoiceSettings,
  invoiceLoading,
  setInvoiceLoading,
  emailLoading,
  setEmailLoading,
  previewLoading,
  setPreviewLoading,
  showConfirmation,
  setShowConfirmation,
  showEmailModal,
  setShowEmailModal,
  emailTo,
  setEmailTo,
  emailSubject,
  setEmailSubject,
  emailMessage,
  setEmailMessage,
  saveInvoiceToCustomers,
  setSaveInvoiceToCustomers,
  cn,
  formatDate,
  handleGeneratePDF,
  pdfLoading,
  setPdfLoading,
  handlePracticeSelection,
  getPractice: memoizedGetPractice
}), [
  // Include dependencies for all the props
  user, selectedPractices, selectedPractice, profileData, 
  showProfileWarning, showProfileSettings, entries, rateType, 
  rateValue, total, summary, previewHtml, showSettings,
  treatmentDate, invoiceSettings, invoiceLoading, emailLoading, 
  previewLoading, showConfirmation, showEmailModal, emailTo,
  emailSubject, emailMessage, saveInvoiceToCustomers, pdfLoading,
  memoizedGetPractice
  // Note: Functions are memoized separately, so don't need to be in this dependency array
]);

// CRITICAL FIX: Address the getPractice function recreation issue
// CRITICAL FIX: Use useRef to create a truly stable version that never changes
const stableGetPractice = useRef(memoizedGetPractice);

// CRITICAL FIX: Reduce prop passing to only essential props
const essentialInvoiceProps = useMemo(() => {
  return {
    user,
    selectedPractices,
    selectedPractice,
    showProfileSettings,
    getPractice: stableGetPractice.current
    // Only include props that the component actually needs to render
    // Add more as needed, but keep this minimal
  };
}, [
  user,
  selectedPractices,
  selectedPractice,
  showProfileSettings
  // Keep dependencies minimal
]);

// COMPONENT RENDER
return (
  <div className="p-4">
    {process.env.NODE_ENV === 'development' && <DebugPanel selectedPractices={selectedPractices} selectedPractice={selectedPractice} />}

    <Tabs defaultValue="monthly" className="space-y-4">
      <TabsList className="bg-[#2A2A3E] border-purple-500/20">
        <TabsTrigger value="monthly">Monthly Invoice</TabsTrigger>
        <TabsTrigger value="daily">Daily Record Log</TabsTrigger>
      </TabsList>

      <TabsContent value="monthly">
        {/* CRITICAL FIX: Use essential props and spread the rest */}
        <MonthlyInvoice 
          {...essentialInvoiceProps}
          {...monthlyInvoiceProps}
        />
      </TabsContent>

      <TabsContent value="daily">
        <DailyRecordLog 
          user={user} 
          selectedPractices={selectedPractices}
          profileData={profileData}
        />
      </TabsContent>
    </Tabs>

    {/* Practice Settings Dialog */}
    <Dialog open={showPracticeSettings} onOpenChange={setShowPracticeSettings}>
      <DialogContent className="bg-[#2A2A3E] border-purple-500/20 text-white max-w-4xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Manage Practices</DialogTitle>
        </DialogHeader>
        <PracticeSettings 
          initialSelectedPractices={selectedPractices}
          onSave={handlePracticeSelection}
          onClose={() => setShowPracticeSettings(false)}
          user={user}
        />
      </DialogContent>
    </Dialog>

    {/* Invoice Settings Dialog */}
    <Dialog open={showSettings} onOpenChange={setShowSettings}>
      <DialogContent className="bg-[#2A2A3E] border-purple-500/20 text-white">
        <DialogHeader>
          <DialogTitle>Invoice Settings</DialogTitle>
          <DialogDescription className="text-gray-300">
            Customize how your invoice appears.
          </DialogDescription>
        </DialogHeader>
        {/* Invoice settings dialog content */}
      </DialogContent>
    </Dialog>
  </div>
);
};

export default HygienistInvoice;